import React from 'react';
import cn from 'classnames';
import {
  Radio,
  type RadioProps,
  RadioGroup,
  type RadioGroupProps,
  RadioProvider,
  type RadioProviderProps,
} from '@ariakit/react';
import { FormField, type FormFieldProps } from '../FormField';
import { ControlLabel } from '../ControlLabel';
import * as styles from './styles.module.css';
import { useSizeContext } from '../Hooks/useSizeContext';

export function Provider(props: RadioProviderProps) {
  return <RadioProvider {...props} />;
}

export function Group(props: RadioGroupProps) {
  return <RadioGroup {...props} />;
}

type ControlProps = Omit<RadioProps, 'size'> & {
  size?: 'medium' | 'large';
  className?: string;
  disableStates?: boolean;
};

export function Control({
  size = 'large',
  className = '',
  disableStates = false,
  ...props
}: ControlProps) {
  const sizeContext = useSizeContext() as 'medium' | 'large';
  return (
    <Radio
      className={cn([
        styles.radioControl,
        styles[sizeContext || size],
        disableStates && styles.disableStates,
        className,
      ])}
      {...props}
    />
  );
}

type GroupContainerProps = {
  formFieldProps: FormFieldProps;
  size?: 'large' | 'medium';
  name: string;
  options: {
    [key: string]: string;
  };
  defaultValue?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

export const GroupContainer = React.forwardRef<HTMLDivElement, GroupContainerProps>(
  ({ formFieldProps, size = 'large', name, options, defaultValue, onChange }, ref) => {
    const keys = Object.keys(options);

    return (
      <Provider defaultValue={defaultValue}>
        <Group render={<FormField {...formFieldProps} tagElement="div" />}>
          <div>
            {keys.map((key) => (
              <ControlLabel label={options[key]} key={key} size={size}>
                <Control name={name} value={key} onChange={onChange} />
              </ControlLabel>
            ))}
          </div>
        </Group>
      </Provider>
    );
  }
);
